import React from 'react';
import { motion } from 'framer-motion';
import { CountUp } from '../utils/countUp';

const stories = [
  {
    title: 'A Royal Affair in Udaipur',
    image: '/img/i33.jpg',
    excerpt: 'An intimate palace wedding with bespoke décor and world-class performances.',
  },
  {
    title: 'Sunset Vows by the Sea',
    image: '/img/i35.jpg',
    excerpt: 'Golden hour promises on a pristine Goan beach, finished with fireworks.',
  },
  {
    title: 'Desert Dreams in Jaisalmer',
    image: '/img/i21.jpg',
    excerpt: 'Sangeet under the stars with a regal Mehfil setting and curated artisans.',
  },
];

const stats = [
  { value: 40, suffix: '+', label: 'Years of Experience' },
  { value: 500, suffix: '+', label: 'Weddings Planned' },
  { value: 100, suffix: '%', label: 'Happy Clients' },
];

export default function StoriesAndStats() {
  return (
    <section className="py-24 bg-gradient-to-b from-[#395958] to-[#2a4342] text-white">
      {/* Stories Section */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-3xl font-serif mb-4"
          >
            Stories That Travel Beyond Borders
          </motion.h3>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-sm leading-relaxed max-w-3xl mx-auto"
            style={{ color: '#F1D291' }}
          >
            Every celebration we design is a one-of-a-kind chapter. Here are glimpses from a few of our favorites.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {stories.map((story, index) => (
            <motion.article
              key={story.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: index * 0.05 }}
              viewport={{ once: true, margin: '-100px' }}
              className="group bg-white/5 border border-white/10 overflow-hidden"
            >
              <div className="relative">
                  <img src={story.image} alt={story.title} className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors" />
                </div>
              <div className="p-6">
                <h4 className="text-xl font-serif mb-3 transition-all duration-300 group-hover:text-2xl">{story.title}</h4>
                <p className="text-sm text-white/70 leading-relaxed mb-6">{story.excerpt}</p>
                <button className="px-6 py-2 border border-white text-white uppercase tracking-[0.3em] text-[10px]">
                  Read Full Story
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: '-100px' }}
          className="text-3xl md:text-4xl font-serif mb-6"
        >
          Crafted Once. Remembered Forever.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true, margin: '-100px' }}
          className="text-sm md:text-base leading-relaxed mb-14"
          style={{ color: '#F1D291' }}
        >
          Not just planners – we're curators of once-in-a-lifetime moments. From our own exclusive estates in breathtaking locales to iconic destinations across the world, every YPD celebration is as unique as a couture creation – designed to be experienced only once, and cherished forever.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-white/10 pt-10 max-w-4xl mx-auto">
          {stats.map((stat, index) => (
            <motion.div
              key={`${stat.label}-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true, margin: '-100px' }}
              className="text-center"
            >
              <div className="text-4xl font-serif mb-3">
                <CountUp end={stat.value} suffix={stat.suffix} />
              </div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/60">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}